import { action, CubePresenter, FlipIntent, Logger } from 'wdc-cube'

import { WaitingScope } from 'src/components/waiting/waiting.scopes'
import { MainPresenter } from 'src/main/Main.presenter'
import { ResultadoAnaliseRow } from 'src/modules/bordero/bordero.types'
import integracaoMonitoramentoService from 'src/modules/integracao/integracao-monitoramento.service'
import IntegracaoMonitoramentoKeys from '../integracao-monitoramento.keys'
import { ResultadoAnaliseVisaoCedenteKeys } from './resultado_analise_visao_cedente.keys'
import ResultadoAnaliseVisaoCedenteScope from './resultado_analise_visao_cedente.scopes'

const LOG = Logger.get('ResultadoAnalisePresenter')

export class ResultadoAnaliseVisaoCedentePresenter extends CubePresenter<
    MainPresenter,
    ResultadoAnaliseVisaoCedenteScope
> {
    // :: Constructor
    public constructor(app: MainPresenter) {
        super(app, new ResultadoAnaliseVisaoCedenteScope())
    }

    // :: Instance
    #visaoCedenteId?: number

    public override release() {
        super.release()
        LOG.debug('Finalized')
    }

    public override async applyParameters(intent: FlipIntent, initialization: boolean): Promise<boolean> {
        const keys = new ResultadoAnaliseVisaoCedenteKeys(this.app, intent)

        keys.dialogSlot(this.scope)

        LOG.debug(`applyParameters ${keys.visaoCedenteId}`)

        if (initialization) {
            await this.#initialize()
        }
        if (this.#visaoCedenteId !== keys.visaoCedenteId) {
            this.#visaoCedenteId = keys.visaoCedenteId
            await this._loadResults()
        }

        return keys.allow
    }

    public override publishParameters(intent: FlipIntent): void {
        const keys = new ResultadoAnaliseVisaoCedenteKeys(this.app, intent)
        keys.visaoCedenteId = this.#visaoCedenteId
        LOG.debug(`publishParameters ${keys.visaoCedenteId}`)
    }

    async #initialize() {
        LOG.debug('initialize')

        this.scope.onClose = this.handleClose.bind(this)
    }

    @action()
    private async handleClose() {
        this.app.scope.dialog = undefined
        await new IntegracaoMonitoramentoKeys(this.app).flip()
    }

    private async _loadResults() {
        try {
            this.scope.waiting = new WaitingScope()

            if (!this.#visaoCedenteId) throw new Error('Id do visão cedente deve ser informado')

            const resultadosBordero = await integracaoMonitoramentoService.fetchResultadosVisaoCedente(
                this.#visaoCedenteId!
            )

            const groupedResults = resultadosBordero.reduce((itens, item) => {
                let analiseItem = itens.find((i) => i.analiseId === item.analiseId)

                if (!analiseItem) {
                    analiseItem = {
                        analiseId: item.analiseId,
                        analiseNome: item.analiseNome,
                        resultadoAnalise: item.resultado,
                        regras: []
                    }
                    itens.push(analiseItem)
                }

                let regra = analiseItem.regras.find((r) => r.id === item.regraId)
                if (!regra) {
                    regra = {
                        id: item.regraId,
                        nome: item.regraNome,
                        rating1: item.rating1,
                        rating2: item.rating2,
                        resultado: item.resultado,
                        resultados: []
                    }
                    analiseItem.regras.push(regra)
                }
                regra.resultados.push(item)

                if (precisaAtualizarStatus(analiseItem.resultadoAnalise, item.resultado)) {
                    analiseItem.resultadoAnalise = item.resultado
                }
                return itens
            }, [] as ResultadoAnaliseRow[])

            this.scope.resultados = agruparPor(groupedResults, 'resultadoAnalise')
        } catch (caught) {
            LOG.error('handleFind', caught)
            throw caught
        } finally {
            this.scope.waiting = undefined
        }
    }
}

export default ResultadoAnaliseVisaoCedentePresenter

const agruparPor = <T, K extends keyof T>(lista: T[], chave: K) => {
    return lista.reduce((acc, item) => {
        const resultado = item[chave] as string
        if (!acc[resultado]) {
            acc[resultado] = []
        }
        acc[resultado].push(item)
        return acc
    }, {} as Record<string, T[]>)
}

const precisaAtualizarStatus = (statusAtual: string, novoStatus: string): boolean => {
    const stAtual = converteStatusEmValor(statusAtual)
    const stNovo = converteStatusEmValor(novoStatus)
    return stNovo > stAtual
}

const converteStatusEmValor = (status: string): number => {
    switch (status) {
        case 'BLOQUEAR':
            return 5
        case 'ALERTAR':
            return 4
        case 'LIBERAR':
            return 3
        case 'SUCESSO':
            return 2
        case 'IGNORADO':
            return 1
        case 'SEM_ACAO':
            return 0
        default:
            throw new Error('Status inválido')
    }
}
